import Vue from 'vue'
import VueRouter from 'vue-router'
import Index from '../views/admin/AdminIndex.vue'
import RouteUnavailable from '../views/admin/RouteUnavailable.vue'
import axios from '../utils/axios'
import store from '../store'
Vue.use(VueRouter)

const BOLD_PREFLIGHT_CACHE_WINDOW_MS = 60 * 1000
const BOLD_ROUTE_LOG_PREFIX = '[BoldListingsRoute]'
let boldPreflightState = {
  checkedAt: 0,
  ok: false,
  reason: 'unchecked'
}

const getBoldPreflightFailureReason = error => {
  const status = error && error.response ? error.response.status : null

  if (status === 401 || status === 419) {
    return 'auth'
  }
  if (status === 403) {
    return 'permission'
  }
  if (status >= 500) {
    return 'server'
  }
  if (error && error.code === 'ECONNABORTED') {
    return 'timeout'
  }

  return 'network'
}

const runBoldListingsPreflight = async () => {
  if (process.env.NODE_ENV !== 'production') {
    console.info(
      `${BOLD_ROUTE_LOG_PREFIX} preflight skipped (non-production env).`
    )
    return { ok: true, reason: 'skipped' }
  }

  const now = Date.now()
  if (
    boldPreflightState.checkedAt &&
    now - boldPreflightState.checkedAt < BOLD_PREFLIGHT_CACHE_WINDOW_MS
  ) {
    console.info(
      `${BOLD_ROUTE_LOG_PREFIX} preflight cache hit`,
      boldPreflightState
    )
    return boldPreflightState
  }

  try {
    console.info(`${BOLD_ROUTE_LOG_PREFIX} preflight started`)
    await axios.get('api/admin/bold-listings?page=1&itemsPerPage=1', {
      timeout: 7000
    })
    boldPreflightState = {
      checkedAt: now,
      ok: true,
      reason: 'ok'
    }
    console.info(`${BOLD_ROUTE_LOG_PREFIX} preflight passed`)
  } catch (error) {
    boldPreflightState = {
      checkedAt: now,
      ok: false,
      reason: getBoldPreflightFailureReason(error)
    }
    console.error(`${BOLD_ROUTE_LOG_PREFIX} preflight failed`, {
      reason: boldPreflightState.reason,
      status: error && error.response ? error.response.status : null,
      message: error && error.message ? error.message : 'unknown'
    })
  }

  return boldPreflightState
}

const routes = [
  {
    path: '/',
    name: 'Admin',
    component: Index,
    redirect: '/dashboard',
    beforeEnter: (to, from, next) => {
      const user = JSON.parse(window.localStorage.getItem('user'))

      if (user) {
        next()
      } else {
        next({ path: '/login' })
      }
    },
    children: [
      {
        path: '/dashboard',
        name: 'Dashboard',
        component: () =>
          import(
            /* webpackChunkName: "Dashboard" */ '../views/admin/Dashboard.vue'
          )
      },
      {
        path: '/employees',
        name: 'Employees',
        component: () =>
          import(
            /* webpackChunkName: "employees" */ '../views/admin/employees/EmployeeIndex.vue'
          )
      },
      {
        path: '/blog-types',
        name: 'BlogTypes',
        component: () =>
          import(
            /* webpackChunkName: "employees" */ '../views/admin/blog_types/BlogTypes.vue'
          )
      },
      {
        path: '/priority-listings',
        name: 'PriorityListings',
        component: () =>
          import(
            /* webpackChunkName: "PriorityListings" */ '../views/admin/priority_listings/PriorityListingIndex.vue'
          )
      },
      {
        path: '/free-listings',
        name: 'FreeListings',
        component: () =>
          import(
            /* webpackChunkName: "FreeListings" */ '../views/admin/free_listings/FreeListingIndex.vue'
          )
      },
      {
        path: '/admin/bold-listings',
        alias: '/bold-listings',
        name: 'BoldListings',
        beforeEnter: async (to, from, next) => {
          console.info(`${BOLD_ROUTE_LOG_PREFIX} entering route`, {
            to: to.path,
            from: from && from.path ? from.path : null
          })
          const preflight = await runBoldListingsPreflight()

          if (preflight.ok) {
            console.info(`${BOLD_ROUTE_LOG_PREFIX} route allowed`)
            next()
            return
          }

          if (preflight.reason === 'auth') {
            console.warn(`${BOLD_ROUTE_LOG_PREFIX} redirecting to /login`)
            next({ path: '/login' })
            return
          }

          console.warn(`${BOLD_ROUTE_LOG_PREFIX} redirecting to fallback page`, {
            reason: preflight.reason
          })
          next({
            name: 'RouteUnavailable',
            query: {
              reason: `bold-preflight-${preflight.reason}`,
              target: to.path
            }
          })
        },
        component: () =>
          import(
            /* webpackChunkName: "BoldListings" */ '../views/admin/bold_listings/BoldListingIndex.vue'
          )
      },
      {
        path: '/admin/unavailable',
        name: 'RouteUnavailable',
        component: RouteUnavailable
      },
      {
        path: '/admin/*',
        component: RouteUnavailable
      },
      {
        path: '/views',
        name: 'Views',
        component: () =>
          import(
            /* webpackChunkName: "user_views" */ '../views/admin/user_views/Index.vue'
          )
      },
      {
        path: '/blogs',
        name: 'Blogs',
        component: () =>
          import(
            /* webpackChunkName: "blogs" */ '../views/admin/blogs/BlogIndex.vue'
          )
      },
      {
        path: '/business',
        name: 'Business',
        component: () =>
          import(
            /* webpackChunkName: "business" */ '../views/admin/business/BusinessIndex.vue'
          )
      },
      {
        path: '/quotes',
        name: 'Quotes',
        component: () =>
          import(
            /* webpackChunkName: "quotes" */ '../views/admin/quotes/QuoteIndex.vue'
          )
      },
      {
        path: '/booking-invoices',
        name: 'Booking',
        component: () =>
          import(
            /* webpackChunkName: "booking_invoices" */ '../views/admin/booking_invoices/BookingIndex.vue'
          )
      },
      {
        path: '/booking-invoices/:id/payment-details',
        name: 'BookingPaymentDetails',
        component: () =>
          import(
            /* webpackChunkName: "booking_payment_details" */ '../views/admin/booking_invoices/BookingPaymentDetails.vue'
          )
      },
      {
        path: '/categories',
        name: 'Category',
        component: () =>
          import(
            /* webpackChunkName: "categories" */ '../views/admin/categories/CategoryIndex.vue'
          )
      },
      {
        path: '/tags',
        name: 'Tag',
        component: () =>
          import(
            /* webpackChunkName: "tags" */ '../views/admin/tags/TagIndex.vue'
          )
      },
      {
        path: '/groups',
        name: 'Group',
        component: () =>
          import(
            /* webpackChunkName: "groups" */ '../views/admin/groups/GroupIndex.vue'
          )
      },
      {
        path: '/suburbs',
        name: 'Suburb',
        component: () =>
          import(
            /* webpackChunkName: "suburbs" */ '../views/admin/suburbs/SuburbIndex.vue'
          )
      },
      {
        path: '/regions',
        name: 'Region',
        component: () =>
          import(
            /* webpackChunkName: "regions" */ '../views/admin/regions/RegionIndex.vue'
          )
      },
      {
        path: '/permissions',
        name: 'Permission',
        component: () =>
          import(
            /* webpackChunkName: "permissions" */ '../views/admin/permissions/PermissionIndex.vue'
          )
      },
      {
        path: '/sales-report',
        name: 'SalesReport',
        component : () => 
          import(
            /* webpackChunkName: "salesreport" */ '../views/admin/sales_report/reports.vue'
          )
      },
      {
        path: '/kpis',
        name: 'Kpis',
        component : () => 
          import(
            /* webpackChunkName: "salesreport" */ '../views/admin/sales_report/Kpis.vue'
          )
      },
      {
        path: '/keywords',
        name: 'Keyword',
        component: () =>
          import(
            /* webpackChunkName: "keywords" */ '../views/admin/keywords/KeywordIndex.vue'
          )
      },
      {
        path: '/phone-calls',
        name: 'PhoneCalls',
        component: () => 
          import(
            /* webpackChunkName: "phone-calls" */ '../views/admin/sales_report/EmployeeCalls.vue'
          )
      },
      {
        path: '/calendar',
        name: 'Calendar',
        component: () => 
          import(
            /* webpackChunkName: "phone-calls" */ '../views/admin/calendar/Calendar.vue'
          )
      },
      {
        path: '/employee-charts/:id',
      name: 'employee-charts',
      props: true,
        component: () => 
          import(
            /* webpackChunkName: "phone-calls" */ '../views/admin/sales_report/EmployeeReport.vue'
          ),
        beforeEnter: (to, from, next) => {
        const user = JSON.parse(window.localStorage.getItem('user'));
        // const role = window.localStorage.getItem('role');
          if (!user) {
            next({ path: '/login' });
          } else if (!store.getters['permissions/getModelPermissions']('salesRep').view_charts) {
            next({ path: '/employees' });
          } else {
            next();
          }
        }
      },
      {
        path: '/employee-calendar/:id',
      name: 'employee-calendar',
      props: true,
        component: () => 
          import(
            /* webpackChunkName: "phone-calls" */ '../views/admin/calendar/EmployeeCalendar.vue'
          ),
        beforeEnter: (to, from, next) => {
        const user = JSON.parse(window.localStorage.getItem('user'));
        // const role = window.localStorage.getItem('role');
          if (!user) {
            next({ path: '/login' });
          } else if (!store.getters['permissions/getModelPermissions']('salesRep').view_charts) {
            next({ path: '/employees' });
          } else {
            next();
          }
        }
      }
    ]
  },

  {
    path: '/login',
    name: 'Login',
    component: () =>
      import(/* webpackChunkName: "Login" */ '../views/Login.vue')
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.onError(error => {
  const message = (error && error.message) || ''
  const chunkLoadError =
    /Loading chunk [\d]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)

  if (chunkLoadError) {
    router.replace({ name: 'RouteUnavailable', query: { reason: 'chunk' } })
  }
})

export default router
